import moment from 'moment-timezone';

import { ChartMode, type SeriesFilter } from './TrafficAnalysis';

import type { TrafficAnalysis as TrafficAnalysisData } from 'sonata-static-storage/lib/entities/footfallReport/footfallReport.types';

type HcPoint = [number, number | null];

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getTodayTimestamp(timezone: string): number {
  return moment.tz(timezone).startOf('day').valueOf();
}

export function getDayStart(ts: number, timezone: string): number {
  return moment.tz(ts, timezone).startOf('day').valueOf();
}

export function buildSeries(
  data: TrafficAnalysisData,
  showPredictions: boolean,
  seriesFilter: SeriesFilter,
  chartMode: ChartMode,
  colors: { baseline: string; uplift: string },
  timezone: string
) {
  const todayTimestamp = getTodayTimestamp(timezone);
  const showBaseline = seriesFilter === 'all' || seriesFilter === 'baseline';
  const showUplift = seriesFilter === 'all' || seriesFilter === 'uplift';
  const isAreas = chartMode === 'areas';
  const type = isAreas ? 'areaspline' : 'column';

  const baselineByTimeStamp = new Map(data.baseline.map(p => [new Date(p.date).getTime(), p.value]));
  const upliftByTimeStamp = new Map(data.uplift.map(p => [new Date(p.date).getTime(), p.value]));
  const baselinePredictionByTimeStamp = new Map(
    data.baselinePrediction.map(p => [new Date(p.date).getTime(), p.value])
  );
  const upliftPredictionByTimeStamp = new Map(data.upliftPrediction.map(p => [new Date(p.date).getTime(), p.value]));

  const allTimestamps = [
    ...new Set([
      ...data.baseline.map(p => new Date(p.date).getTime()),
      ...(showPredictions ? data.baselinePrediction.map(p => new Date(p.date).getTime()) : [])
    ])
  ].sort((a, b) => a - b);

  const baseline: HcPoint[] = [];
  const uplift: HcPoint[] = [];
  const baselinePrediction: HcPoint[] = [];
  const upliftPrediction: HcPoint[] = [];

  // In bars mode, an hour with no data at all would otherwise render as a blank gap
  // indistinguishable from missing chart data — a 0 (with minPointLength) draws a thin
  // sliver instead so the hour slot is still visible.
  const missingRealValueFallback = isAreas ? null : 0;

  for (const ts of allTimestamps) {
    const isReal = ts < todayTimestamp;
    const isForecast = ts >= todayTimestamp;
    // Past dates with no real measurement (e.g. after the campaign's real-data window
    // ends but before today) fall back to the modeled value, so the solid line has no gaps.
    const baselineValue = isReal
      ? (baselineByTimeStamp.get(ts) ?? baselinePredictionByTimeStamp.get(ts) ?? missingRealValueFallback)
      : null;
    const baselinePredValue = isForecast ? (baselinePredictionByTimeStamp.get(ts) ?? null) : null;
    const rawUplift = isReal
      ? (upliftByTimeStamp.get(ts) ?? upliftPredictionByTimeStamp.get(ts) ?? missingRealValueFallback)
      : null;
    const rawUpliftPred = isForecast ? (upliftPredictionByTimeStamp.get(ts) ?? null) : null;

    // When baseline is also visible, the uplift series carries the cumulative value
    // (baseline + raw_uplift) so it visually "stacks" on top of the baseline area/bar.
    // When viewing uplift alone, use the raw value — there is no baseline to stack on.
    const upliftValue =
      showBaseline && rawUplift !== null && baselineValue !== null ? baselineValue + rawUplift : rawUplift;
    const upliftPredValue =
      showBaseline && rawUpliftPred !== null && baselinePredValue !== null
        ? baselinePredValue + rawUpliftPred
        : rawUpliftPred;

    baseline.push([ts, baselineValue]);
    uplift.push([ts, upliftValue]);
    baselinePrediction.push([ts, baselinePredValue]);
    upliftPrediction.push([ts, upliftPredValue]);
  }

  if (isAreas) {
    // Bridge at today: extend both real series to the first forecast point so the
    // solid and dashed areas share the same boundary and connect visually.
    const firstPredIdx = allTimestamps.findIndex(ts => ts >= todayTimestamp);
    if (firstPredIdx !== -1) {
      const firstPredTs = allTimestamps[firstPredIdx];
      const bBridge = baselinePredictionByTimeStamp.get(firstPredTs) ?? null;
      const uRawBridge = upliftPredictionByTimeStamp.get(firstPredTs) ?? null;
      baseline[firstPredIdx] = [firstPredTs, bBridge];
      uplift[firstPredIdx] = [
        firstPredTs,
        showBaseline && bBridge !== null && uRawBridge !== null ? bBridge + uRawBridge : uRawBridge
      ];
    }
  }

  const stacking = undefined;
  const predType = isAreas ? 'areaspline' : 'column';
  const predBaselineColor = isAreas ? colors.baseline : hexToRgba(colors.baseline, 0.5);
  const predUpliftColor = isAreas ? colors.uplift : hexToRgba(colors.uplift, 0.5);

  const baselineSeries = {
    color: colors.baseline,
    connectNulls: false,
    data: baseline,
    fillOpacity: 0.15,
    lineWidth: 2,
    name: 'Baseline Visits',
    opacity: 0.5,
    stacking,
    type,
    visible: showBaseline
  };
  const upliftSeries = {
    color: colors.uplift,
    connectNulls: false,
    data: uplift,
    fillOpacity: 0.15,
    lineWidth: 2,
    name: 'Attributed Uplift',
    opacity: 0.5,
    stacking,
    type,
    visible: showUplift
  };
  const baselinePredictionSeries = {
    color: predBaselineColor,
    connectNulls: false,
    dashStyle: 'Dash',
    data: baselinePrediction,
    fillOpacity: 0.08,
    lineWidth: 2,
    marker: { enabled: false },
    name: 'Baseline Prediction',
    opacity: 0.2,
    stacking,
    type: predType,
    visible: showBaseline && showPredictions
  };
  const upliftPredictionSeries = {
    color: predUpliftColor,
    connectNulls: false,
    dashStyle: 'Dash',
    data: upliftPrediction,
    fillOpacity: 0.08,
    lineWidth: 2,
    marker: { enabled: false },
    name: 'Uplift Prediction',
    opacity: 0.2,
    stacking,
    type: predType,
    visible: showUplift && showPredictions
  };

  // Uplift (cumulative, taller) drawn first as background; baseline on top.
  return [upliftSeries, baselineSeries, upliftPredictionSeries, baselinePredictionSeries];
}
